import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import PermIdentityIcon from '@mui/icons-material/PermIdentity'
import CloseIcon from '@mui/icons-material/Close'

import { Avatar, Box, Dialog, DialogTitle, Grid, Fab, Typography, useTheme, IconButton } from '@mui/material'

import { useGET_mySchoolQuery } from 'redux/api/mySchoolApi'
import {
  useGET_studentLearningStyleQuestionsQuery,
  useGET_studentsProfileCountersQuery,
} from 'redux/api/studentsApi'

import AnchorButton from 'components/atoms/buttons/AnchorButton'
import AvatarForm from 'components/organisms/forms/AvatarForm'
import StudentLearningStyleForm from 'components/organisms/forms/StudentLearningStyleForm'
import LearningStylePieChart from 'components/utilized/student/LearningStylePieChart'

import { useMedia } from 'utils/customHooks/useMedia'
import { ELEMENT_RADIUS_MID, PRIMARY_GREEN, PRIMARY_TRANSPARENT_GREEN, PRIMARY_TRANSPARENT_ORANGE, PRIMARY_YELLOW, PURE_BLACK, PURE_WHITE } from 'utils/customizedTheme'

import balloon1 from 'assets/svg/speech-bubble.svg'
import balloon2 from 'assets/svg/think-bubble.svg'
import profile from 'assets/images/student/bee-laying.png'
import takeSurveyIcons from 'assets/images/student/take-survey-icons.png'



import CSS from 'csstype';
import { setOpenAvatar } from 'redux/ui/dashboardUI'
import { setUser } from 'redux/ui/common/userUI'
import { useGET_userQuery } from 'redux/api/userAPI'
import { AppDispatch, RootState } from 'redux/store'
import { translateGradeLevel } from 'utils/helpers'
import AvatarInParts from 'components/utilized/studentDashboard/AvatarInParts'

const StudentWelcomePage = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()

  const { isUnder600px, isUnder900px, isUnder1200px, isUnder1536px } = useMedia()

  // Get user authorization data only if authentication token is set in Redux store
  const {
    data: user,
  } = useGET_userQuery()
  

  useEffect(() => {
    // Redirect already authenticated users to the app
    if (user) {
      dispatch(setUser(user))
    }
  }, [user])


  const openAvatar = useSelector((state: RootState) => state['dashboard'].open_avatar)

  const { data: counters } = useGET_studentsProfileCountersQuery()
  const { data: school } = useGET_mySchoolQuery()
  const { data: learningStyle } = useGET_studentLearningStyleQuestionsQuery()
  const [openSurvey, setOpenSurvey] = useState(false)

  const bubbleTextStyle: CSS.Properties = {
    color: '#000',
    fontWeight: '400',
    fontSize: isUnder1536px
      ? isUnder1200px
        ? isUnder900px
          ? isUnder600px
            ? '0.7rem'
            : '1rem'
          : '0.8rem'
        : '0.9rem'
      : '1rem',
  }

  const bubbleNameStyle: CSS.Properties = {
    margin: 'auto',
    width: '60%',
    paddingRight: '15%',
    fontWeight: '400',
    fontSize: isUnder1536px
      ? isUnder1200px
        ? isUnder900px
          ? isUnder600px
            ? '1rem'
            : '1.6rem'
          : '1.2rem'
        : '1.2rem'
      : '1.5rem',
  }

  const bubbleStyle: CSS.Properties = {
    backgroundPosition: `center center`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    position: 'relative'
  }

  const bubbleWrapperStyle: CSS.Properties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
  }

  const sectionTitleStyle: CSS.Properties = {
    width: '100%',
    margin: '1rem',
    borderRadius: ELEMENT_RADIUS_MID,
    padding: '0.5rem',
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25), 0px 4px 4px rgba(0, 0, 0, 0.25)'
  }

  const sectionCircleStyle: CSS.Properties = {
    display: 'inline-block',
    borderRadius: '50%',
    width: '45px',
    height: '40px',
    lineHeight: '40px',
    fontSize: '1.5rem',
    verticalAlign: 'middle',
    fontWeight: 'bold',
    textAlign: 'center',
    marginRight: '30px'
  }

  const sectionTextStyle: CSS.Properties = {
    fontSize: '1.5rem',
    fontWeight: '400',
    height: '40px',
    lineHeight: '40px',
    verticalAlign: 'middle'
  }

  const chartData = learningStyle?.categories.map((category) => {
    return {
      name: category.title,
      value: category.percentage,
      fill: category.color_hex_code
    }
  })

  return (
    <>
      <Grid container rowSpacing={1} columnSpacing={1} my={5}>
        {/* Balloons */}
        <Grid item xs={12} lg={6} sx={{ position: 'relative', top: '-50px'}}>
          <Grid container rowSpacing={1} columnSpacing={1} sx={{
              position: 'relative',
              bottom: '-15%',
              left: isUnder1200px ? '0%' : '15%'
            }}>
            <Grid item xs={5}>
              {/* Second balloon */}
              <Box className="transparent-background"
                  sx={{ 
                    ...bubbleStyle,
                    backgroundImage: `url(${balloon1})`,
                    paddingTop: '90%',
                    position: 'relative',
                    right: '-20%',
                    bottom: '-5%'
                  }}>
                  <Box
                    className="transparent-background"
                    sx={{
                      ...bubbleWrapperStyle,
                      paddingBottom: '30%'
                    }}
                  >
                    <Typography sx={bubbleNameStyle}>{t('assignments.hey', { first_name: `${user?.first_name}` })}</Typography>
                  </Box>
                </Box>
            </Grid>
            <Grid item xs={7}>
              {/* Second balloon */}
                <Box className="transparent-background" 
                  sx={{ 
                    ...bubbleStyle,
                    backgroundImage: `url(${balloon2})`,
                    paddingTop: '90%',
                  }}>
                  <Box
                    className="transparent-background"
                    sx={{
                      ...bubbleWrapperStyle,
                      backgroundSize: '120%',
                      paddingBottom: '20%',
                    }}
                  >
                    <Typography sx={bubbleTextStyle}>{`${t('common.today-you-have')}:`}</Typography>
                    <AnchorButton to='/student-assignments'  text={`${counters?.assignments_counter} ${t('common.new-assignments')}`} sx={bubbleTextStyle} />
                    <AnchorButton to='/announcements'  text={`${counters?.announcements_counter} ${t('common.new-announcements')}`} sx={bubbleTextStyle} />
                  </Box>
                </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ textAlign: 'center' }}>
            {/* Bee */}
            <img alt='EsyBee' style={{ width: '70%' }} src={profile} />
          </Grid>      
        </Grid>


        {/* Profile & Learning Style */}
        <Grid
          item
          xs={12}
          lg={6}
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '1rem' }}
        >
          {/* Profile */}
          <Box 
            className="transparent-background"
            sx={{ flex: 1, width: '100%', padding: '0 1rem 1rem 1rem' }}>
            <Box
              className="transparent-background"
              sx={{
                ...sectionTitleStyle,
                backgroundColor: PRIMARY_TRANSPARENT_GREEN,
              }}
            >
              <Typography>
                <span style={{ 
                  ...sectionCircleStyle,
                  backgroundColor: PRIMARY_GREEN,
                }}>1</span><span style={sectionTextStyle}>
                  {`${t('common.profile')}`}
                </span></Typography>
            </Box>

            <Grid container columnSpacing={2} rowSpacing={3} my={3}>
              <Grid item xs={12} md={6} sx={{ textAlign: isUnder900px ? 'center' : 'right' }}>



              {!user?.student_profile?.avatar &&
                <Avatar
                  sx={{
                    bgcolor: PRIMARY_TRANSPARENT_GREEN,
                    width: 200,
                    height: 200,
                    display: 'inline-block',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                  variant='circular'
                  onClick={() => dispatch(setOpenAvatar(true))}
                  src={user?.student_profile?.avatar}
                >
                  <PermIdentityIcon sx={{ width: 200, height: 200 }} />
                  <Typography color={PURE_WHITE} sx={{ 
                    color: '#000',
                    position: 'absolute',
                    bottom: '20px',
                    fontSize: '30px',
                    lineHeight: '34px',
                    textTransform: 'uppercase',
                    textAlign: 'center'
                  }}>{t('student.create-avatar')}</Typography>
                </Avatar> }
                
                {user?.student_profile?.avatar && user.student_profile?.avatar_parts && 
                    <span onClick={() => dispatch(setOpenAvatar(true)) } style = {{ cursor: 'pointer' }}>
                      <AvatarInParts parts={user.student_profile.avatar_parts} />
                    </span>
                }  


              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: isUnder900px ? 'center' : 'right' }}>
                <Box
                  className="transparent-background"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    height: '100%'
                  }}
                >
                  <Typography sx={{fontSize: '1.2rem', textAlign: 'center' }}>{t('common.name')}: <b>{`${user?.first_name} ${user?.last_name}`}</b></Typography>
                  <Typography sx={{fontSize: '1.2rem', textAlign: 'center' }} my={2}><b>{`${translateGradeLevel(user?.student_profile.grade_level)}`}</b></Typography>
                  <Typography sx={{fontSize: '1.2rem', textAlign: 'center' }}><b>{`${school?.title}`}</b></Typography>
                </Box>
              </Grid>
            </Grid>

            <Dialog
              onClose={() => dispatch(setOpenAvatar(false))}
              open={openAvatar}
              fullWidth
              maxWidth='xl'
              PaperProps={{
                sx: {
                  margin: 0,
                  padding: 0,
                  width: { xs: 'calc(100vw - 40px)', md: '80vw' },
                  maxWidth: { xs: 'calc(100vw - 40px)', md: '80vw' },
                  background: theme.palette.background.default,
                },
              }}
            >
                <DialogTitle
                sx={{
                  width: '100%',
                  backgroundColor: PRIMARY_TRANSPARENT_GREEN,
                  fontFamily: 'NewsReader',
                  fontWeight: '600',
                  fontSize: '1.75rem',
                }}
              >
                {t('student.create-avatar')}
                <IconButton
                        onClick={() => dispatch(setOpenAvatar(false))}
                        aria-label='close'
                        sx={{
                          color: PURE_BLACK,
                          position: 'absolute',
                          top: '0px',
                          right: '0px',
                        }}
                      >
                    <CloseIcon sx={{ fontSize: '2rem' }} />
                </IconButton>
              </DialogTitle>
              <AvatarForm />
            </Dialog>
          </Box>
          {/* Learning style */}
          <Box 
            className="transparent-background"
            sx={{ flex: 1, width: '100%', padding: '0 1rem 1rem 1rem' }}>
            <Box
              className="transparent-background"
              my={5}
              sx={{
                ...sectionTitleStyle,
                backgroundColor: '#fcb9f7',
              }}
            >
              <Typography>
                <span style={{ 
                  ...sectionCircleStyle,
                  backgroundColor: '#fff',
                }}>2</span><span style={sectionTextStyle}>
                  {`${t('student.learning-styles')}`}
                </span></Typography>
            </Box>


            <Grid container columnSpacing={2} rowSpacing={3} my={5} sx={{ alignItems: 'center' }}>
              <Grid item xs={12} md={6} sx={{ textAlign: isUnder900px ? 'center' : 'right' }}>
                  {learningStyle?.is_taken ? (
                    <Box className="transparent-background" sx={{display: 'inline-block'}}  onClick={() => setOpenSurvey(true)} >
                      <LearningStylePieChart chartData={chartData} width={200}/>
                    </Box>
                  ) : (
                  <Fab
                    onClick={() => setOpenSurvey(true)}
                    variant='circular'
                    aria-label={t('common.take-a-survey')}
                    size='large'
                    sx={{ 
                      backgroundColor: 'rgb(232, 103, 73) !important',
                      color: '#fff !important',
                      boxShadow: '0px 0px 16px 4px rgba(0,0,0,0.4)', 
                      width: '150px', 
                      height: '150px', 
                      borderRadius: '50% !important', 
                      fontWeight: 'bold',
                      fontSize: '1.5rem !important', 
                      lineHeight: '1.5rem !important', 
                    }}
                  >
                    {t('common.take-a-survey')}
                  </Fab>
                  )} 
              </Grid>
              <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
                 <img alt='EsyBee' style={{ width: '150px' }} src={takeSurveyIcons} />
              </Grid>
            </Grid>
            
            <Dialog
              onClose={() => setOpenSurvey(false)}
              open={openSurvey}
              fullWidth
              maxWidth='xl'
              PaperProps={{
                sx: {
                  margin: 0,
                  padding: 0,
                  width: { xs: 'calc(100vw - 40px)', md: '80vw' },
                  maxWidth: { xs: 'calc(100vw - 40px)', md: '80vw' },
                  background: theme.palette.background.default,
                  position: 'relative',
                },
              }}
            >
              <DialogTitle
                sx={{
                  width: '100%',
                  backgroundColor: PRIMARY_TRANSPARENT_GREEN,
                  fontFamily: 'NewsReader',
                  fontWeight: '600',
                  fontSize: '1.75rem',
                }}
              >
                <IconButton
                        onClick={() => setOpenSurvey(false)}
                        aria-label='close'
                        sx={{
                          color: PURE_BLACK,
                          position: 'absolute',
                          top: '0px',
                          right: '0px',
                        }}
                      >
                    <CloseIcon sx={{ fontSize: '2rem' }} />
                </IconButton>
                {t('student.define-your-learning-style')}
              </DialogTitle>
              <StudentLearningStyleForm />
            </Dialog>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
export default StudentWelcomePage
